import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiFilm, FiHome, FiPlus, FiUploadCloud } from "react-icons/fi";
import type { UploadQueueItem } from "../types";
import { createVideoThumbnail } from "../services/thumbnailService";
import { MAX_BULK_FILES, validateClipFile } from "../services/validation";
import { uploadClip } from "../services/clipService";
import { friendlyFirebaseError } from "../utils";
import { UploadQueue } from "../components/UploadQueue";
import { AuthButton } from "../components/AuthButton";
import { ToastRegion } from "../components/ToastRegion";
import { ThemeToggle } from "../components/ThemeToggle";
import { useAuthUser } from "../hooks/useAuthUser";
import { useClipTheme } from "../hooks/useClipTheme";
import { useToasts } from "../hooks/useToasts";
import "../clips.css";

function createLocalId(): string {
  return typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

function titleFromFilename(filename: string): string {
  return filename.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
}

export default function UploadPage() {
  const { user, loading: authLoading } = useAuthUser();
  const { theme, toggleTheme } = useClipTheme();
  const { toasts, addToast, dismissToast } = useToasts();
  const [items, setItems] = useState<UploadQueueItem[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef(items);

  const updateItems = (updater: (current: UploadQueueItem[]) => UploadQueueItem[]) => {
    setItems((current) => {
      const next = updater(current);
      itemsRef.current = next;
      return next;
    });
  };

  useEffect(() => () => {
    itemsRef.current.forEach((item) => URL.revokeObjectURL(item.thumbnailPreviewUrl));
  }, []);

  const updateItem = (id: string, changes: Partial<UploadQueueItem>) => {
    updateItems((current) => current.map((item) => item.localId === id ? { ...item, ...changes } : item));
  };

  const addFiles = async (fileList: FileList | File[]) => {
    if (!user) {
      addToast("Sign in before selecting clips.", "error");
      return;
    }
    const available = MAX_BULK_FILES - itemsRef.current.length;
    const selected = Array.from(fileList);
    if (available <= 0) {
      addToast("The queue can hold up to 10 clips.", "error");
      return;
    }
    if (selected.length > available) addToast(`Only the first ${available} clips were added.`, "info");
    const accepted = selected.slice(0, available);
    const valid: File[] = [];
    accepted.forEach((file) => {
      const validationError = validateClipFile(file);
      if (validationError) addToast(validationError, "error");
      else valid.push(file);
    });
    if (!valid.length) return;

    setPreparing(true);
    for (const file of valid) {
      try {
        const { thumbnail, durationSeconds } = await createVideoThumbnail(file);
        const item: UploadQueueItem = {
          localId: createLocalId(),
          file,
          thumbnail,
          thumbnailPreviewUrl: URL.createObjectURL(thumbnail),
          durationSeconds,
          title: titleFromFilename(file.name),
          description: "",
          recordingDate: "",
          status: "waiting",
          progress: 0,
          etaSeconds: null,
        };
        updateItems((current) => [...current, item]);
      } catch (error) {
        addToast(`${file.name}: ${friendlyFirebaseError(error)}`, "error");
      }
    }
    setPreparing(false);
  };

  const removeItem = (id: string) => {
    const item = itemsRef.current.find((candidate) => candidate.localId === id);
    if (item) URL.revokeObjectURL(item.thumbnailPreviewUrl);
    updateItems((current) => current.filter((candidate) => candidate.localId !== id));
  };

  const runUpload = async (item: UploadQueueItem): Promise<boolean> => {
    if (!user) return false;
    updateItem(item.localId, { status: "uploading", progress: 0, etaSeconds: null, error: undefined });
    try {
      await uploadClip(item, user, {
        onProgress: (progress, etaSeconds) => updateItem(item.localId, { progress, etaSeconds }),
      });
      updateItem(item.localId, { status: "success", progress: 100, etaSeconds: 0 });
      return true;
    } catch (error) {
      updateItem(item.localId, { status: "failed", error: friendlyFirebaseError(error), etaSeconds: null });
      return false;
    }
  };

  const uploadAll = async () => {
    if (!user) {
      addToast("Sign in before uploading clips.", "error");
      return;
    }
    const waiting = itemsRef.current.filter((item) => item.status === "waiting");
    const valid = waiting.filter((item) => {
      if (item.title.trim()) return true;
      updateItem(item.localId, { status: "failed", error: "Add a title before uploading." });
      return false;
    });
    if (!valid.length) {
      addToast("There are no clips ready to upload.", "info");
      return;
    }

    setUploading(true);
    let cursor = 0;
    let completed = 0;
    const worker = async () => {
      while (cursor < valid.length) {
        const item = valid[cursor++];
        if (await runUpload(item)) completed += 1;
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, valid.length) }, worker));
    setUploading(false);
    if (completed) addToast(`${completed} ${completed === 1 ? "clip" : "clips"} uploaded successfully.`, "success");
    if (completed < valid.length) addToast("Some clips failed. You can retry them individually.", "error");
  };

  const retryItem = async (id: string) => {
    const item = itemsRef.current.find((candidate) => candidate.localId === id);
    if (!item || uploading) return;
    if (!item.title.trim()) {
      updateItem(id, { error: "Add a title before uploading." });
      return;
    }
    if (!user) {
      addToast("Sign in before retrying this clip.", "error");
      return;
    }
    setUploading(true);
    const succeeded = await runUpload(item);
    setUploading(false);
    addToast(succeeded ? "Clip uploaded successfully." : "The retry failed. Check the error and try again.", succeeded ? "success" : "error");
  };

  return (
    <main className="clips-app upload-page" data-clip-theme={theme}>
      <header className="clips-header">
        <Link to="/clips" className="clips-brand"><span><FiFilm /></span><div><b>NOVA GANG</b><small>CLIPS</small></div></Link>
        <nav aria-label="Clips navigation"><Link to="/"><FiHome /> Home</Link><Link to="/clips">Discover</Link><Link className="active" to="/clips/upload"><FiUploadCloud /> Upload</Link></nav>
        <div className="clip-header-actions">
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          {user && <AuthButton user={user} onError={(message) => addToast(message, "error")} />}
        </div>
      </header>

      <section className="clips-content upload-content">
        <Link to="/clips" className="clip-back"><FiArrowLeft /> Back to clips</Link>
        <div className="upload-hero">
          <h1>Upload clips</h1>
        </div>

        {authLoading && <div className="clip-auth-loading" aria-label="Checking sign-in status"><i className="clip-spinner" /></div>}
        {!authLoading && !user && (
          <div className="clip-auth-notice"><AuthButton user={user} onError={(message) => addToast(message, "error")} /></div>
        )}

        {user && (
        <>
        <div
          className={`upload-dropzone ${preparing ? "preparing" : ""}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => { event.preventDefault(); if (!uploading) void addFiles(event.dataTransfer.files); }}
        >
          <input
            ref={inputRef}
            type="file"
            accept="video/*"
            multiple
            hidden
            onChange={(event) => { if (event.target.files) void addFiles(event.target.files); event.target.value = ""; }}
          />
          <span className="upload-drop-icon">{preparing ? <i className="clip-spinner" /> : <FiUploadCloud />}</span>
          <div><h2>{preparing ? "Preparing previews…" : "Select clips"}</h2><p>10 videos max · 150 MB each</p></div>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={preparing || uploading || items.length >= MAX_BULK_FILES}><FiPlus /> Select videos</button>
        </div>

        {items.length > 0 && (
          <>
            <UploadQueue items={items} onChange={updateItem} onRemove={removeItem} onRetry={retryItem} busy={uploading} />
            <div className="upload-submit-bar">
              <span>{items.filter((item) => item.status === "waiting").length} ready</span>
              <button type="button" onClick={uploadAll} disabled={uploading || preparing || !items.some((item) => item.status === "waiting")}>
                {uploading ? <><i className="clip-mini-spinner" /> Uploading…</> : <><FiUploadCloud /> Upload all</>}
              </button>
            </div>
          </>
        )}
        </>
        )}
      </section>
      <ToastRegion toasts={toasts} onDismiss={dismissToast} />
    </main>
  );
}
